import { useEffect, useState } from 'react';
import { create } from 'zustand';
import type { FlowWorkingState } from '@shared/flow/view-state';
import type { FlowViewStateKey, FlowViewStateStore } from '../views/useViewState';
import { isPlaySectionSurface, type ContactMount } from './useContactMount';
import { registerIdentityScopedReset } from '../../../lib/identity-scoped-reset';

/**
 * In-session working state for the three "People in <Play>" section mounts
 * (plan `2026-10-06-plays-tab-ux-followups-plan.md`, item 1 — Spencer's
 * 2026-10-06 answer: "on first load it should reset state but then on click
 * it carries over").
 *
 * ── The bug this closes ────────────────────────────────────────────────────
 * Each section mount owns ONE server `flow_view_state` row
 * (`contact_play_responded` / `contact_play_not_responded` /
 * `contact_play_candidates`) shared across every play AND every session —
 * per-play rows would multiply unboundedly, so the sharing is deliberate and
 * stays (`useViewState.ts`'s module doc). Restoring that row's WORKING half
 * on every mount is what made all three sections read "Modified" the instant
 * a rep selected any play: `baseline` is recomputed fresh from the preset +
 * the Mine-group seed, while `working` came back verbatim from a row written
 * weeks ago under a different play — and "Modified" is a live diff of the
 * two, not a stored flag (`useViewStateMachine.ts`'s `isDirty`). Any one-time
 * divergence (a sort click, the "(Mine)" chip removed once to look at
 * everyone) came back as a permanent orange cluster in EVERY play until the
 * rep hit Reset. It is the same shape the account drill-down had (Annie Hale,
 * 2026-08-27) that `skipWorkingRestore` was introduced for.
 *
 * ── Why not just `skipWorkingRestore` (the plan's Option A) ────────────────
 * Because the server row was ALSO doing real work: plan 2026-09-25-003 U1
 * deliberately leaned on it so a rep's sort/filters survive a section
 * collapse/reopen (`LedgerSection` unmounts the table while folded) and a
 * play switch (`PlaysTab` mounts `PlayPeopleSections` under `key={playId}`,
 * so every switch is a full remount). Option A alone would have traded the
 * false "Modified" for losing both of those.
 *
 * This module splits the two concerns the one row was conflating:
 *  - CROSS-SESSION state is no longer restored at all. `deriveContactMount`
 *    reports `restoreWorkingState: false` for a section, so a fresh page load
 *    lands on the clean preset + Mine seed. No false Modified, ever.
 *  - WITHIN a session, this module-scope cache is the restore source. It is
 *    seeded by the live machine on every edit and read back by the next mount
 *    of that section, so collapse/reopen and play switches carry over exactly
 *    as they did — but from state this very session produced, against a
 *    baseline computed by the same `adaptOnOpen` under the same identity, so
 *    a clean section restores clean BY CONSTRUCTION. Only an edit the rep
 *    actually made can read Modified.
 *
 * The server row keeps being WRITTEN (`useFlowViewStatePersist`, untouched)
 * and its `defaultViewId` pointer keeps being READ (rung 3) — same bargain
 * the account mount struck.
 *
 * ── Keyed by section, not by play ──────────────────────────────────────────
 * One entry per `stateKey`, so an edit made under play A is still in force
 * when the rep clicks play B ("on click it carries over"). That is exactly
 * what the shared server row used to do within a session; the only thing
 * being taken away is its ability to reach ACROSS sessions, which is the
 * half that was lying. A per-`(stateKey, playId)` key was considered and
 * rejected: it would make every newly clicked play land clean, which is the
 * forward direction of "carries over" and the one a rep actually watches.
 *
 * ── Deliberately non-reactive ──────────────────────────────────────────────
 * Nothing subscribes. Readers take a one-shot snapshot at MOUNT
 * (`usePlaySectionSessionSeed`) and writers push through `.getState()`, so a
 * write can never re-render a live section and re-apply its own state on top
 * of the rep's in-flight edit. It is a zustand store rather than a bare
 * module `Map` because CLAUDE.md's "Zustand for Module-Scope Client State"
 * rule admits no exceptions for new stores, and because it makes the cache
 * resettable from a test with the house `setState(initialState)` form.
 */
export interface PlaySectionSessionView {
  /** The view the section was standing on when this was recorded (preset slug or saved-view id). */
  activeViewId: string;
  /** Its live working half — byte-identical to what `useFlowViewStatePersist` sends to the server. */
  workingState: FlowWorkingState;
}

export interface PlaySectionSessionViewState {
  /** One entry per section `stateKey`; empty on every fresh page load, which is the whole point. */
  views: Partial<Record<FlowViewStateKey, PlaySectionSessionView>>;
  /** Records a section's live view state. Last write wins; no merge. */
  remember: (stateKey: FlowViewStateKey, view: PlaySectionSessionView) => void;
}

/** Exported per the CLAUDE.md zustand convention — tests reset with the merge form `setState(initialState)` (a replace would delete `remember`). */
export const initialState: Pick<PlaySectionSessionViewState, 'views'> = {
  views: {},
};

export const usePlaySectionSessionViewStore = create<PlaySectionSessionViewState>((set) => ({
  ...initialState,
  remember: (stateKey, view) => set(state => ({ views: { ...state.views, [stateKey]: view } })),
}));

// This cache holds per-identity data (a rep's own in-session sort/filter
// edits) exactly like `FlowChatSessionsProvider` and `impersonate-write-mode`
// do, so it registers the same way: a same-tab sign-out/sign-in or
// impersonation switch must not let the next identity inherit the previous
// one's section state. Merge form (`setState(initialState)`), not the
// replace form — a replace would delete `remember`.
registerIdentityScopedReset(() => usePlaySectionSessionViewStore.setState(initialState));

/** The three `stateKey`s a GLOBAL play-section mount can carry (`CONTACT_PLAY_SECTION_STATE_KEYS` in `@shared/flow/play-people`, re-listed as `FlowViewStateKey` literals so this file doesn't import the shared `Record<PlayPeopleSection, string>` just to narrow it). */
const PLAY_SECTION_STATE_KEYS: ReadonlySet<FlowViewStateKey> = new Set([
  'contact_play_responded',
  'contact_play_not_responded',
  'contact_play_candidates',
]);

/**
 * A GLOBAL play-section mount — not merely a mount whose `surface` is one of
 * the three section surfaces. `isPlaySectionSurface(mount.surface)` alone is
 * also true for the (unreachable today, but not guarded against)
 * `deriveContactMount({ accountId, surface: 'play-responded' })` combination:
 * `deriveContactMount` resolves that to `stateKey: 'contact_account'` with
 * `restoreWorkingState: false`, specifically so a shared `contact_account`
 * row never replays one account's filters onto another (`useContactMount.ts`'s
 * "Annie Hale" precedent). Checking `stateKey` instead of bare `surface`
 * closes that gap for this cache too — it must never record or restore for a
 * `contact_account`-keyed mount, only for the three real global section rows.
 */
function isPlaySectionMount(mount: Pick<ContactMount, 'surface' | 'stateKey'>): boolean {
  return isPlaySectionSurface(mount.surface) && PLAY_SECTION_STATE_KEYS.has(mount.stateKey);
}

/**
 * Wraps a `FlowViewStateStore` BEFORE `useViewStateMachine` sees it — the
 * same seam `withoutLegacyPlayCandidatesWorkingState` occupies, and composed
 * after it.
 *
 * Non-section mounts are returned by reference, unchanged. A section mount
 * ALWAYS has the server row's working half replaced, never merged:
 *  - with a session entry, by that entry (the machine then restores it at
 *    rung 2, or through the `?view_<stateKey>=` param rung when the param
 *    names the same view);
 *  - without one (the first mount of a page load), by nulls — so resolution
 *    falls through to the `defaultViewId` pointer and then the code preset.
 *
 * Nulling is belt-and-braces: `skipWorkingRestore` (derived from
 * `mount.restoreWorkingState`) already blocks the restore on that path. Doing
 * it here too makes this function total, so the "a section never replays a
 * stored row" invariant is readable in one place and testable without the
 * machine.
 */
export function withPlaySectionSessionWorkingState(
  store: FlowViewStateStore,
  mount: Pick<ContactMount, 'surface' | 'stateKey'>,
  seed: PlaySectionSessionView | null,
): FlowViewStateStore {
  if (!isPlaySectionMount(mount)) return store;
  const restored = store.restored ?? { activeViewId: null, defaultViewId: null, workingState: null };
  return {
    ...store,
    restored: {
      ...restored,
      activeViewId: seed?.activeViewId ?? null,
      workingState: seed?.workingState ?? null,
    },
  };
}

/**
 * The session cache entry this mount should restore from, snapshotted ONCE at
 * mount and never updated afterwards.
 *
 * A `useState` initializer rather than a subscription on purpose: the value
 * is a restore SEED, consumed by the machine's one-shot first resolution. A
 * live subscription would hand a mounted section its own echo every time it
 * wrote (and every time a SIBLING section wrote), which is both pointless and
 * a re-render loop risk. Re-seeding happens the only way it should — by the
 * component remounting (collapse/reopen, or `key={playId}` on a play switch).
 */
export function usePlaySectionSessionSeed(
  mount: Pick<ContactMount, 'surface' | 'stateKey'>,
): PlaySectionSessionView | null {
  const [seed] = useState<PlaySectionSessionView | null>(() => (
    isPlaySectionMount(mount)
      ? usePlaySectionSessionViewStore.getState().views[mount.stateKey] ?? null
      : null
  ));
  return seed;
}

/**
 * The write half — the session twin of `useFlowViewStatePersist`, and
 * deliberately NOT debounced like it is. The server PUT can afford to
 * coalesce a second of keystrokes because the row only has to be right
 * eventually; this cache has to be right at the instant the rep clicks
 * another play, which unmounts the section that owns the state. Writing
 * synchronously on every committed change means the entry is always current
 * before any unmount can happen.
 *
 * A no-op on every non-section mount, and before the machine has resolved
 * (`enabled` is `machine.isReady`) — recording the pre-resolution placeholder
 * would overwrite a real entry with an empty one.
 */
export function useRememberPlaySectionSessionView(
  mount: Pick<ContactMount, 'surface' | 'stateKey'>,
  options: { enabled: boolean; activeViewId: string; working: FlowWorkingState },
): void {
  const { enabled, activeViewId, working } = options;
  const { surface, stateKey } = mount;
  useEffect(() => {
    if (!enabled || !isPlaySectionMount({ surface, stateKey })) return;
    usePlaySectionSessionViewStore.getState().remember(stateKey, { activeViewId, workingState: working });
  }, [enabled, surface, stateKey, activeViewId, working]);
}
